import {
  Box,
  Button,
  Checkbox,
  Flex,
  Icon,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Radio,
  RadioGroup,
  Skeleton,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  DarwinFeeType,
  DarwinPropertyDetailField,
  DarwinVendorType,
  ParticipantRole,
  TransactionSide,
} from '@client/graphql/__generated__/types';
import { FieldType, InputFormatType } from '@common/enums';
import { getDarwinFieldMappings } from '@common/external/darwin/darwin-field-mappings';
import { DarwinEnabledNormalizedFields } from '@common/external/darwin/darwin.fields';
import { ValueType } from '@common/graphql';
import { formatToCurrency } from '@common/utils/formatter';
import dayjs from 'dayjs';
import { ChevronDown, ChevronUp, Lock, TriangleAlert } from 'lucide-react';
import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import type {
  DarwinFeesSyncPreview,
  DarwinFeeSyncInput,
  DarwinPropertyDetailKey,
  DarwinPropertyDetailSelection,
  DarwinPropertyDetailValues,
  DarwinPropertyWithParticipants,
  DarwinStatusSyncPreview,
  DarwinVendorsSyncPreview,
  MlsListingSelectionResult,
  PropertyOption,
  VendorSelections,
} from '~/apps/extension/pages/Compliance/context/hooks/useDarwinSync';
import { Select } from '~/services/document/common/Select';

/**
 * Builds a composite key from a key term and Darwin field key.
 * Format: "keyTerm:darwinKey" (e.g., "purchase_price:sellingPrice")
 */
const buildCompositeKey = (keyTerm: string, darwinKey: string): string => {
  return `${keyTerm}:${darwinKey}`;
};

/**
 * Splits a composite key into its constituent parts.
 * @param compositeKey - The composite key in format "keyTerm:darwinKey"
 * @returns Object containing keyTerm and darwinKey
 */
const splitCompositeKey = (
  compositeKey: string
): { keyTerm: string; darwinKey: string } => {
  const [keyTerm, darwinKey] = compositeKey.split(':');

  return { keyTerm, darwinKey };
};

/**
 * Default display value for missing or null data.
 */
const DEFAULT_DISPLAY_VALUE = '—';

/**
 * Converts a value to a number or returns null if conversion fails.
 * Handles both numeric and string inputs safely.
 */
const toNumber = (value: ValueType | undefined): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

/**
 * Commission field groups that define relationships between percentage and amount fields.
 * Used to derive one value from the other when combined with purchase price.
 * NOTE: When both percentage and amount are present, percentage takes precedence.
 */
const COMMISSION_FIELD_GROUPS: Array<{
  percentKey: DarwinEnabledNormalizedFields;
  amountKey: DarwinEnabledNormalizedFields;
}> = [
  {
    percentKey:
      DarwinEnabledNormalizedFields.BUYER_BROKER_COMPENSATION_FROM_SELLER_PERCENT,
    amountKey:
      DarwinEnabledNormalizedFields.BUYER_BROKER_COMPENSATION_FROM_SELLER,
  },
  {
    percentKey:
      DarwinEnabledNormalizedFields.LISTING_BROKER_COMPENSATION_PERCENT,
    amountKey: DarwinEnabledNormalizedFields.LISTING_BROKER_COMPENSATION,
  },
];
const PROPERTY_DETAIL_LABELS: Record<DarwinPropertyDetailKey, string> = {
  classificationName: 'Accounting Code',
  propertyTypeName: 'Property Type',
  typeCodeName: 'Type Code',
};

const PROPERTY_DETAIL_FIELD_MAP: Record<
  DarwinPropertyDetailKey,
  DarwinPropertyDetailField
> = {
  typeCodeName: DarwinPropertyDetailField.TYPE_CODE,
  propertyTypeName: DarwinPropertyDetailField.PROPERTY_TYPE,
  classificationName: DarwinPropertyDetailField.CLASSIFICATION,
};

export interface ParticipantInfo {
  label: string;
  value: string;
  role: ParticipantRole;
}

interface DarwinUploadPopoverProps {
  children: ReactNode;
  onUpload?: (
    selectedFields: string[],
    selectedParticipants: ParticipantRole[],
    selectedPropertyId?: string | null,
    selectedPropertyDetails?: DarwinPropertyDetailSelection | null,
    syncStatus?: boolean
  ) => Promise<string | null>;
  onVendorSync?: (
    selectedVendorFields: string[],
    selectedPropertyId: string
  ) => Promise<void>;
  onVendorSelectionChange?: (
    vendorType: DarwinVendorType,
    personId: number
  ) => void;
  onFeeSync?: (
    selectedFees: DarwinFeeSyncInput[],
    selectedPropertyId: string
  ) => Promise<void>;
  keyTermValues?: Record<string, ValueType>;
  propertyDetails?: DarwinPropertyDetailValues;
  statusSyncPreview?: DarwinStatusSyncPreview | null;
  vendorsSyncPreview?: DarwinVendorsSyncPreview | null;
  feesSyncPreview?: DarwinFeesSyncPreview | null;
  vendorSelections?: VendorSelections;
  vendorSyncLoading?: boolean;
  feeSyncLoading?: boolean;
  syncPreviewLoading?: boolean;
  participants?: ParticipantInfo[];
  darwinPropertyLoading: boolean;
  darwinProperties?: DarwinPropertyWithParticipants[] | undefined;
  selectedProperty?: PropertyOption | null;
  onPropertyChange?: (property: PropertyOption | null) => void;
  propertyOptions: PropertyOption[];
  transactionSide?: TransactionSide | null;
  variant: 'external' | 'internal';
  /** MLS listing selection result with ranked listings for creating a new Darwin property */
  mlsListingForNewProperty?: MlsListingSelectionResult | null;
  mlsListingForNewPropertyLoading?: boolean;
  /** Currently selected MLS listing ID for creating a new Darwin property */
  selectedMlsListingIdForNewProperty?: string | null;
  /** Callback when MLS listing selection changes for creating a new Darwin property */
  onMlsListingChangeForNewProperty?: (listingId: string | null) => void;
}

export const DarwinUploadPopover: FC<DarwinUploadPopoverProps> = ({
  children,
  onUpload,
  onVendorSync,
  onVendorSelectionChange,
  onFeeSync,
  keyTermValues,
  propertyDetails,
  statusSyncPreview,
  vendorsSyncPreview,
  feesSyncPreview,
  vendorSelections,
  syncPreviewLoading = false,
  participants = [],
  darwinPropertyLoading,
  darwinProperties,
  selectedProperty,
  onPropertyChange,
  propertyOptions,
  transactionSide,
  variant,
  mlsListingForNewProperty,
  mlsListingForNewPropertyLoading = false,
  selectedMlsListingIdForNewProperty,
  onMlsListingChangeForNewProperty,
}) => {
  const visibleKeyTermKeys = useMemo(() => {
    return ((values: Record<string, ValueType> | undefined): string[] => {
      if (!values) {
        return [];
      }

      return Object.keys(values);
    })(keyTermValues);
  }, [keyTermValues]);

  // Memoize field mappings to avoid repeated calls to getDarwinFieldMappings
  const fieldMappingsCache = useMemo(() => {
    const cache = new Map<string, ReturnType<typeof getDarwinFieldMappings>>();
    visibleKeyTermKeys.forEach((fieldKey) => {
      const mappings = getDarwinFieldMappings(
        fieldKey as DarwinEnabledNormalizedFields,
        transactionSide
      );
      cache.set(fieldKey, mappings);
    });

    return cache;
  }, [visibleKeyTermKeys, transactionSide]);

  // Compute allowed composite keys based on visible fields and transaction side
  const allowedCompositeKeys = useMemo(() => {
    if (!keyTermValues) {
      return new Set<string>();
    }

    const keys = new Set<string>();

    visibleKeyTermKeys.forEach((fieldKey) => {
      const fieldConfigs = fieldMappingsCache.get(fieldKey);

      if (!fieldConfigs || fieldConfigs.length === 0) {
        return;
      }

      fieldConfigs.forEach((config) => {
        keys.add(buildCompositeKey(fieldKey, config.darwinKey));
      });
    });

    return keys;
  }, [keyTermValues, visibleKeyTermKeys, fieldMappingsCache]);

  // Track previous allowed keys to identify newly allowed keys
  const previousAllowedKeysRef = useRef<Set<string>>(new Set());

  // Store both key term field and darwin field key as "keyTerm:darwinKey"
  const [selectedFields, setSelectedFields] = useState<string[]>(() => {
    if (!keyTermValues) return [];

    // Select all visible Darwin fields by default
    return visibleKeyTermKeys.flatMap((fieldKey) => {
      const fieldConfigs = fieldMappingsCache.get(fieldKey);

      if (!fieldConfigs || fieldConfigs.length === 0) {
        return [];
      }

      return fieldConfigs.map((config) =>
        buildCompositeKey(fieldKey, config.darwinKey)
      );
    });
  });
  const [selectedParticipants, setSelectedParticipants] = useState<
    ParticipantRole[]
  >(() => {
    // Select all participants by default
    return participants.map((p) => p.role);
  });
  const [isUploading, setIsUploading] = useState(false);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Update selected fields when allowed keys change
  useEffect(() => {
    if (allowedCompositeKeys.size === 0) {
      setSelectedFields([]);
      previousAllowedKeysRef.current = new Set();

      return;
    }

    const previousAllowedKeys = previousAllowedKeysRef.current;
    const newlyAllowedKeys = Array.from(allowedCompositeKeys).filter(
      (key) => !previousAllowedKeys.has(key)
    );
    previousAllowedKeysRef.current = new Set(allowedCompositeKeys);

    setSelectedFields((prev) => {
      // Filter out keys that are no longer allowed
      const filtered = prev.filter((compositeKey) =>
        allowedCompositeKeys.has(compositeKey)
      );

      // Add newly allowed keys
      const next = [...filtered];
      newlyAllowedKeys.forEach((key) => {
        if (!next.includes(key)) {
          next.push(key);
        }
      });

      // Return previous state if no changes to avoid unnecessary re-renders
      if (
        next.length === prev.length &&
        next.every((key, index) => key === prev[index])
      ) {
        return prev;
      }

      return next;
    });
  }, [allowedCompositeKeys]);
  const selectedDarwinProperty = useMemo(() => {
    if (!selectedProperty?.value) {
      return null;
    }

    return (
      darwinProperties?.find(
        (p) => p.propertyId.toString() === selectedProperty.value
      ) ?? null
    );
  }, [darwinProperties, selectedProperty?.value]);

  const options: PropertyOption[] = useMemo(() => {
    return [
      {
        value: '',
        label: 'Create Property Record',
      },
      ...propertyOptions,
    ];
  }, [propertyOptions]);

  /**
   * Type-safe accessor for Darwin property detail fields.
   * Maps DarwinPropertyDetailKey to the corresponding field on DarwinPropertyWithParticipants.
   */
  const getDarwinPropertyDetailValue = (
    property: DarwinPropertyWithParticipants | null,
    key: DarwinPropertyDetailKey
  ): string | null | undefined => {
    if (!property) {
      return null;
    }

    // Type-safe mapping of detail keys to actual property fields
    switch (key) {
      case 'typeCodeName':
        return property.typeCodeName;
      case 'propertyTypeName':
        return property.propertyTypeName;
      case 'classificationName':
        return property.classificationName;
      default: {
        // Exhaustiveness check - TypeScript will error if we add a new key without handling it
        const _exhaustiveCheck: never = key;

        return _exhaustiveCheck;
      }
    }
  };

  const propertyDetailComparisons = useMemo(() => {
    if (!propertyDetails) {
      return [] as Array<{
        key: DarwinPropertyDetailKey;
        label: string;
        complianceValue: string;
        darwinValue: string;
        hasChange: boolean;
        rawValue: string | null;
      }>;
    }

    return (
      Object.entries(propertyDetails) as Array<
        [DarwinPropertyDetailKey, string | null | undefined]
      >
    )
      .filter(([, value]) => value !== undefined && value !== null)
      .map(([key, value]) => {
        const complianceValue =
          value === null || value === '' ? '—' : String(value);
        const darwinValueRaw = getDarwinPropertyDetailValue(
          selectedDarwinProperty,
          key
        );
        const darwinValue =
          darwinValueRaw === null ||
          darwinValueRaw === undefined ||
          darwinValueRaw === ''
            ? '—'
            : String(darwinValueRaw);

        return {
          key,
          label: PROPERTY_DETAIL_LABELS[key],
          complianceValue,
          darwinValue,
          hasChange: complianceValue !== darwinValue,
          rawValue: value ?? null,
        };
      });
  }, [propertyDetails, selectedDarwinProperty]);

  const isSellSide = transactionSide === TransactionSide.SELL;
  const isCreatingDarwinProperty = !selectedProperty?.value;

  // Status sync preview from the new dedicated query
  const hasComputedStatus =
    statusSyncPreview != null && statusSyncPreview.proposedValue.value !== null;
  const isStatusUnknownFallback =
    statusSyncPreview?.proposedValue.warningMessage != null;
  // Status is required when creating a new Darwin property (Darwin API requires statusId)
  const isStatusRequired =
    statusSyncPreview?.proposedValue.disabledOptions?.isRequired === true;

  // Status comparison for display - use statusSyncPreview directly
  const statusComparison = useMemo(() => {
    if (!statusSyncPreview) {
      return {
        hasChange: false,
        computedValue: DEFAULT_DISPLAY_VALUE,
        darwinValue: DEFAULT_DISPLAY_VALUE,
      };
    }

    return {
      hasChange: statusSyncPreview.hasChange,
      computedValue:
        statusSyncPreview.proposedValue.value ?? DEFAULT_DISPLAY_VALUE,
      darwinValue:
        statusSyncPreview.currentValue?.value ?? DEFAULT_DISPLAY_VALUE,
    };
  }, [statusSyncPreview]);

  const [shouldSyncPropertyDetails, setShouldSyncPropertyDetails] =
    useState<boolean>(
      () => !isSellSide && propertyDetailComparisons.length > 0
    );

  // Status sync: default to checked unless status is unknown fallback
  const [shouldSyncStatus, setShouldSyncStatus] = useState<boolean>(
    () => hasComputedStatus && !isStatusUnknownFallback
  );

  // Vendor sync: track selected vendor fields (lender, title company)
  // Default to selected if confidence is HIGH and field has a value
  const [selectedVendorFields, setSelectedVendorFields] = useState<string[]>(
    () => {
      if (!vendorsSyncPreview?.fields) return [];

      return vendorsSyncPreview.fields
        .filter(
          (field) =>
            !field.proposedValue.disabledOptions?.isDisabled &&
            !field.proposedValue.disabledOptions?.isDisabledByDefault
        )
        .map((field) => field.label);
    }
  );

  // Track which vendor rows are expanded to show alternatives
  const [expandedVendors, setExpandedVendors] = useState<Set<string>>(
    new Set()
  );

  // Fee sync: track selected fee types
  // Default to selected if field has a change and is not disabled
  const [selectedFeeTypes, setSelectedFeeTypes] = useState<Set<DarwinFeeType>>(
    () => {
      if (!feesSyncPreview?.fields) return new Set();

      const defaultSelected = new Set<DarwinFeeType>();
      feesSyncPreview.fields.forEach((field) => {
        if (
          field.hasChange &&
          !field.proposedValue.disabledOptions?.isDisabled
        ) {
          defaultSelected.add(field.feeType);
        }
      });

      return defaultSelected;
    }
  );

  const toggleFeeSelected = (feeType: DarwinFeeType) => {
    setSelectedFeeTypes((prev) => {
      const next = new Set(prev);
      if (next.has(feeType)) {
        next.delete(feeType);
      } else {
        next.add(feeType);
      }

      return next;
    });
  };

  const toggleVendorExpanded = (label: string) => {
    setExpandedVendors((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }

      return next;
    });
  };

  const hasPropertyDetailsSelection =
    !isSellSide &&
    !isCreatingDarwinProperty &&
    shouldSyncPropertyDetails &&
    propertyDetailComparisons.length > 0;

  useEffect(() => {
    if (isOpen) {
      setShouldSyncPropertyDetails(
        !isSellSide &&
          !isCreatingDarwinProperty &&
          propertyDetailComparisons.length > 0
      );
      // Reset status sync: default to checked unless status is unknown fallback
      setShouldSyncStatus(hasComputedStatus && !isStatusUnknownFallback);
      // Reset vendor selections: select fields with HIGH confidence
      setSelectedVendorFields(
        vendorsSyncPreview?.fields
          ?.filter(
            (field) =>
              !field.proposedValue.disabledOptions?.isDisabled &&
              !field.proposedValue.disabledOptions?.isDisabledByDefault
          )
          .map((field) => field.label) ?? []
      );
      // Reset expanded vendors
      setExpandedVendors(new Set());
      // Reset fee selections: select fees that have changes and are not disabled
      setSelectedFeeTypes(() => {
        if (!feesSyncPreview?.fields) return new Set<DarwinFeeType>();

        const defaultSelected = new Set<DarwinFeeType>();
        feesSyncPreview.fields.forEach((field) => {
          if (
            field.hasChange &&
            !field.proposedValue.disabledOptions?.isDisabled
          ) {
            defaultSelected.add(field.feeType);
          }
        });

        return defaultSelected;
      });
      // Reset participant selections: select all by default
      setSelectedParticipants(
        participants
          .filter((p) => {
            // Listing agents are unchecked by default for SELL-side when updating
            // an existing property. Darwin properties for sell-side transactions are
            // usually created by the MLS <> Darwin integration, which already
            // associates the listing agent, so we don't want to accidentally overwrite.
            if (
              p.role === ParticipantRole.LISTING_AGENT &&
              isSellSide &&
              !isCreatingDarwinProperty
            ) {
              return false;
            }

            return true;
          })
          .map((p) => p.role)
      );
    }
  }, [
    isOpen,
    isSellSide,
    isCreatingDarwinProperty,
    propertyDetailComparisons,
    hasComputedStatus,
    isStatusUnknownFallback,
    vendorsSyncPreview?.fields,
    feesSyncPreview?.fields,
    participants,
  ]);

  const formatValueByType = (
    value: ValueType,
    fieldType: FieldType,
    inputFormat?: { type: InputFormatType }
  ) => {
    if (value === null || value === undefined) {
      return DEFAULT_DISPLAY_VALUE;
    }

    switch (fieldType) {
      case FieldType.NUMBER:
        const numericValue = Number(value);
        if (Number.isNaN(numericValue)) {
          return String(value);
        }

        if (inputFormat?.type === InputFormatType.CURRENCY) {
          return formatToCurrency(numericValue);
        }

        if (inputFormat?.type === InputFormatType.PERCENTAGE) {
          return `${numericValue.toFixed(2)}%`;
        }

        return new Intl.NumberFormat('en-US', {
          maximumFractionDigits: 2,
        }).format(numericValue);
      case FieldType.DATE:
        const parsed = dayjs(String(value));

        return parsed.isValid()
          ? parsed.format('MMM D, YYYY')
          : DEFAULT_DISPLAY_VALUE;
      default:
        return String(value);
    }
  };

  const getFieldComparisons = (fieldKey: DarwinEnabledNormalizedFields) => {
    const fieldConfigs = fieldMappingsCache.get(fieldKey);
    if (!fieldConfigs || fieldConfigs.length === 0 || !keyTermValues) {
      return [];
    }

    let keyTermValue = keyTermValues[fieldKey];

    const commissionGroup = COMMISSION_FIELD_GROUPS.find(
      (group) => group.percentKey === fieldKey || group.amountKey === fieldKey
    );

    if (commissionGroup) {
      const percentValue = toNumber(
        keyTermValues?.[commissionGroup.percentKey]
      );
      const amountValue = toNumber(keyTermValues?.[commissionGroup.amountKey]);
      const purchasePriceValue = toNumber(
        keyTermValues?.[DarwinEnabledNormalizedFields.PURCHASE_PRICE]
      );

      // Derive percentage from amount if percentage is missing
      if (
        commissionGroup.percentKey === fieldKey &&
        percentValue === null &&
        amountValue !== null &&
        purchasePriceValue !== null &&
        purchasePriceValue !== 0
      ) {
        keyTermValue = (amountValue / purchasePriceValue) * 100;
      }

      // Derive or override amount from percentage
      // NOTE: Percentage always takes precedence when both are present
      if (
        commissionGroup.amountKey === fieldKey &&
        percentValue !== null &&
        purchasePriceValue !== null
      ) {
        keyTermValue = (percentValue / 100) * purchasePriceValue;
      }
    }

    // Create a comparison for each Darwin field config
    return fieldConfigs.map((config) => {
      const formattedKeyTermValue = config.fieldType
        ? formatValueByType(keyTermValue, config.fieldType, config.inputFormat)
        : keyTermValue;

      // Use the label from the config instead of deriving from darwinKey
      const displayName =
        config.label ||
        config.darwinKey
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, (str) => str.toUpperCase())
          .trim();

      if (!selectedDarwinProperty) {
        // No property selected or creating new property
        return {
          darwinKey: config.darwinKey,
          hasChange: true,
          keyTermValue: formattedKeyTermValue,
          darwinValue: DEFAULT_DISPLAY_VALUE,
          displayName,
        };
      }

      const darwinValue: unknown =
        selectedDarwinProperty[
          config.darwinKey as keyof DarwinPropertyWithParticipants
        ];

      const adjustedDarwinValue =
        config.fieldType === FieldType.NUMBER &&
        config.inputFormat?.type === InputFormatType.PERCENTAGE &&
        typeof darwinValue === 'number'
          ? darwinValue * 100
          : darwinValue;

      const formattedDarwinValue = config.fieldType
        ? formatValueByType(
            adjustedDarwinValue as ValueType,
            config.fieldType,
            config.inputFormat
          )
        : adjustedDarwinValue;

      const hasChange = formattedKeyTermValue !== formattedDarwinValue;

      return {
        darwinKey: config.darwinKey,
        hasChange,
        keyTermValue: formattedKeyTermValue,
        darwinValue: formattedDarwinValue as
          | string
          | number
          | boolean
          | string[]
          | undefined,
        displayName,
      };
    });
  };

  const getParticipantComparison = (
    participant: ParticipantInfo
  ): { hasChange: boolean; participantValue: string; darwinValue: string } => {
    // Find the selected Darwin property
    const participantValue = participant.value;

    if (!selectedDarwinProperty || !selectedDarwinProperty.participants) {
      // No property selected or creating new property or no participants data
      return {
        hasChange: true,
        participantValue,
        darwinValue: DEFAULT_DISPLAY_VALUE,
      };
    }

    // Map participant role to Darwin participants array
    let darwinParticipants:
      | Array<{
          personName: string;
        }>
      | undefined;

    if (participant.role === ParticipantRole.BUYERS_AGENT) {
      darwinParticipants =
        selectedDarwinProperty.participants.buyersAgents ?? [];
    } else if (participant.role === ParticipantRole.LISTING_AGENT) {
      darwinParticipants =
        selectedDarwinProperty.participants.listingAgents ?? [];
    } else if (participant.role === ParticipantRole.BUYER) {
      darwinParticipants = selectedDarwinProperty.participants.buyers ?? [];
    } else if (participant.role === ParticipantRole.SELLER) {
      darwinParticipants = selectedDarwinProperty.participants.sellers ?? [];
    } else if (participant.role === ParticipantRole.TRANSACTION_COORDINATOR) {
      // For TCs, use isMatched for comparison instead of name matching
      const darwinTcs =
        selectedDarwinProperty.participants.transactionCoordinators ?? [];
      const darwinValue =
        darwinTcs.length > 0
          ? darwinTcs.map((tc) => tc.personName).join(', ')
          : DEFAULT_DISPLAY_VALUE;

      // Check if any Darwin TC matches the Indigo TC using backend matching
      const hasMatch = darwinTcs.some((tc) => tc.isMatched);

      return {
        hasChange: !hasMatch,
        participantValue,
        darwinValue,
      };
    }

    if (!darwinParticipants || darwinParticipants.length === 0) {
      return {
        hasChange: true,
        participantValue,
        darwinValue: DEFAULT_DISPLAY_VALUE,
      };
    }

    // Join Darwin participant names for comparison
    const darwinValue = darwinParticipants.map((p) => p.personName).join(', ');

    const hasChange = participantValue !== darwinValue;

    return {
      hasChange,
      participantValue,
      darwinValue,
    };
  };

  const handleFieldToggle = (compositeKey: string) => {
    // Extract the key term from the composite key (e.g., "purchase_price:sellingPrice" -> "purchase_price")
    const { keyTerm } = splitCompositeKey(compositeKey);

    // Find all Darwin fields for this key term
    const fieldConfigs = fieldMappingsCache.get(keyTerm);

    if (!fieldConfigs || fieldConfigs.length === 0) {
      return;
    }

    // Create composite keys for all Darwin fields of this key term
    const allCompositeKeys = fieldConfigs.map((config) =>
      buildCompositeKey(keyTerm, config.darwinKey)
    );

    setSelectedFields((prev) => {
      // Check if any of the related fields are currently selected
      const isAnySelected = allCompositeKeys.some((key) => prev.includes(key));

      if (isAnySelected) {
        // Uncheck all related fields
        return prev.filter((key) => !allCompositeKeys.includes(key));
      } else {
        // Check all related fields
        return [...prev, ...allCompositeKeys];
      }
    });
  };

  const handleParticipantToggle = (role: ParticipantRole) => {
    setSelectedParticipants((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  const handleVendorFieldToggle = (fieldLabel: string) => {
    setSelectedVendorFields((prev) =>
      prev.includes(fieldLabel)
        ? prev.filter((f) => f !== fieldLabel)
        : [...prev, fieldLabel]
    );
  };

  /**
   * Gets all vendor candidates for a field (best match + alternatives).
   * Returns an array with the best match first, followed by alternatives.
   */
  const getVendorCandidates = (
    field: NonNullable<typeof vendorsSyncPreview>['fields'][number]
  ): Array<{ personId: number; personName: string }> => {
    const candidates: Array<{ personId: number; personName: string }> = [];

    // Add best match first if it exists
    if (
      field.proposedValue.matchedPersonId &&
      field.proposedValue.matchedPersonName
    ) {
      candidates.push({
        personId: field.proposedValue.matchedPersonId,
        personName: field.proposedValue.matchedPersonName,
      });
    }

    // Add alternatives
    if (field.proposedValue.alternatives) {
      // Filter out duplicates (in case best match is also in alternatives)
      const bestMatchId = field.proposedValue.matchedPersonId;
      const uniqueAlternatives = field.proposedValue.alternatives.filter(
        (alt) => alt.personId !== bestMatchId
      );
      candidates.push(
        ...uniqueAlternatives.map((alt) => ({
          personId: alt.personId,
          personName: alt.personName,
        }))
      );
    }

    return candidates;
  };

  /**
   * Gets the currently selected person ID for a vendor field.
   * Returns user's selection if set, otherwise the best match from preview.
   */
  const getSelectedPersonId = (
    field: NonNullable<typeof vendorsSyncPreview>['fields'][number]
  ): number | null => {
    const { vendorType } = field;
    if (vendorType && vendorSelections?.[vendorType]) {
      return vendorSelections[vendorType] ?? null;
    }

    return field.proposedValue.matchedPersonId ?? null;
  };

  /**
   * Handles vendor person selection change.
   */
  const handleVendorPersonChange = (
    vendorType: DarwinVendorType | null | undefined,
    personId: number
  ) => {
    if (vendorType && onVendorSelectionChange) {
      onVendorSelectionChange(vendorType, personId);
    }
  };

  const handleUpload = async () => {
    // Status is selected if required (forced) or user opted in
    const hasStatusSelection =
      hasComputedStatus && (isStatusRequired || shouldSyncStatus);
    const hasVendorSelection = selectedVendorFields.length > 0;
    const hasFeeSelection = selectedFeeTypes.size > 0;
    const hasNonVendorSelections =
      selectedFields.length > 0 ||
      selectedParticipants.length > 0 ||
      hasPropertyDetailsSelection ||
      hasStatusSelection;

    if (!hasNonVendorSelections && !hasVendorSelection && !hasFeeSelection) {
      return;
    }

    // Prevent vendor/fee-only sync when no Darwin property is selected
    const propertyId = selectedProperty?.value || null;
    if (
      !hasNonVendorSelections &&
      (hasVendorSelection || hasFeeSelection) &&
      !propertyId
    ) {
      toast({
        title: 'Select a Darwin property to sync',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });

      return;
    }

    setIsUploading(true);
    try {
      // Track effective property ID - may be updated if we create a new property
      let effectivePropertyId = propertyId;

      // Sync non-vendor items through the existing syncKeyTerms mutation
      if (hasNonVendorSelections && onUpload) {
        // Extract unique key term fields from composite keys (keyTerm:darwinKey)
        // If any Darwin field for a key term is selected, include that key term
        const keyTermFields = Array.from(
          new Set(
            selectedFields.map(
              (compositeKey) => splitCompositeKey(compositeKey).keyTerm
            )
          )
        );

        // Property details selection semantics:
        // - undefined: not applicable (creating new property, can't sync details)
        // - null: explicitly not syncing (property exists but user chose not to sync)
        // - Array: explicitly syncing (property exists and user chose to sync these fields)
        const propertyDetailsSelection = isCreatingDarwinProperty
          ? undefined
          : !isSellSide && shouldSyncPropertyDetails
            ? Array.from(
                new Set(
                  propertyDetailComparisons.map(
                    (comparison) => PROPERTY_DETAIL_FIELD_MAP[comparison.key]
                  )
                )
              )
            : null;

        // Pass status sync preference (required status is always synced)
        const syncStatusValue = hasComputedStatus
          ? isStatusRequired || shouldSyncStatus
          : undefined;

        const newEntityId = await onUpload(
          keyTermFields,
          selectedParticipants,
          propertyId,
          propertyDetailsSelection,
          syncStatusValue
        );

        // Use newly created property ID for vendor/fee sync if we didn't have one
        if (newEntityId && !effectivePropertyId) {
          effectivePropertyId = newEntityId;
        }
      }

      // Sync vendors through the new vendor-specific mutation
      if (hasVendorSelection && onVendorSync && effectivePropertyId) {
        await onVendorSync(selectedVendorFields, effectivePropertyId);
      }

      // IMPORTANT: Fee sync must run AFTER key terms/participant sync completes.
      // Transaction management fees are stored on agent deduction records in Darwin.
      // If participants haven't been synced yet, the agent won't be linked to the
      // property and fee sync will fail silently. The await on onUpload() above
      // ensures proper ordering.
      if (
        hasFeeSelection &&
        onFeeSync &&
        effectivePropertyId &&
        feesSyncPreview?.fields
      ) {
        const feesToSync: DarwinFeeSyncInput[] = feesSyncPreview.fields
          .filter((field) => selectedFeeTypes.has(field.feeType))
          .map((field) => ({
            feeType: field.feeType,
            previewData: field,
          }));

        if (feesToSync.length > 0) {
          await onFeeSync(feesToSync, effectivePropertyId);
        }
      }

      onClose();
    } catch (error) {
      const description =
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred.';

      toast({
        title: 'Failed to upload to Darwin',
        status: 'error',
        description,
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const totalSelections =
    selectedFields.length +
    selectedParticipants.length +
    (hasPropertyDetailsSelection ? propertyDetailComparisons.length : 0) +
    (hasComputedStatus && (isStatusRequired || shouldSyncStatus) ? 1 : 0) +
    selectedVendorFields.length +
    selectedFeeTypes.size;

  return (
    <Popover
      isLazy
      isOpen={isOpen}
      lazyBehavior="unmount"
      size="md"
      onClose={onClose}
      onOpen={onOpen}
    >
      <PopoverTrigger>{children}</PopoverTrigger>
      <Portal>
        <PopoverContent
          bg="compliance.tooltip.bg"
          border="1px solid"
          borderColor="compliance.border.primary"
          boxShadow="lg"
          maxW="350px"
        >
          <PopoverCloseButton color="compliance.text.secondary" py={5} />
          <PopoverHeader py={3}>
            Choose which fields to write to Darwin
          </PopoverHeader>
          <PopoverBody fontWeight="normal" p={4} textTransform="none">
            <VStack align="stretch" spacing={4}>
              {!darwinPropertyLoading && darwinProperties?.length === 0 && (
                <Box
                  border="1px solid"
                  borderColor="yellow.500"
                  borderRadius="md"
                  p={3}
                >
                  <Flex align="center" gap={3}>
                    <Icon as={TriangleAlert} color="yellow.500" size={20} />
                    <Text color="yellow.500">
                      We couldn't find a property record, a new one will be
                      created.
                    </Text>
                  </Flex>
                </Box>
              )}

              {/* Warning boxes - consistent location above Select with loading skeletons */}
              {/* Darwin properties warning (when updating existing property) */}
              {!isCreatingDarwinProperty &&
                (darwinPropertyLoading ? (
                  <Skeleton borderRadius="md" height="40px" />
                ) : (
                  darwinProperties &&
                  darwinProperties.length > 1 && (
                    <Box
                      border="1px solid"
                      borderColor="blue.500"
                      borderRadius="md"
                      p={2}
                    >
                      <Flex align="center" gap={2}>
                        <Icon as={TriangleAlert} boxSize={4} color="blue.500" />
                        <Text color="blue.500" fontSize="xs">
                          Multiple Darwin properties found. Please confirm which
                          one to update.
                        </Text>
                      </Flex>
                    </Box>
                  )
                ))}

              {/* MLS listings warning (when creating new property) */}
              {isCreatingDarwinProperty &&
                (mlsListingForNewPropertyLoading ? (
                  <Skeleton borderRadius="md" height="40px" />
                ) : (
                  (mlsListingForNewProperty?.rankedListings.length ?? 0) >
                    1 && (
                    <Box
                      border="1px solid"
                      borderColor="blue.500"
                      borderRadius="md"
                      p={2}
                    >
                      <Flex align="center" gap={2}>
                        <Icon as={TriangleAlert} boxSize={4} color="blue.500" />
                        <Text color="blue.500" fontSize="xs">
                          Multiple MLS listings found. Please confirm which one
                          to use.
                        </Text>
                      </Flex>
                    </Box>
                  )
                ))}

              <Select<PropertyOption>
                isDisabled={options.length <= 1}
                options={options}
                placeholder="Choose a property..."
                value={selectedProperty}
                onChange={onPropertyChange}
              />

              {/* MLS Listing Selection - show when creating new property */}
              {isCreatingDarwinProperty &&
                (mlsListingForNewPropertyLoading ||
                  (mlsListingForNewProperty?.rankedListings.length ?? 0) >
                    0) && (
                  <VStack align="stretch" spacing={2}>
                    <Text
                      color="compliance.text.primary"
                      fontSize="xs"
                      fontWeight="semibold"
                      textTransform="uppercase"
                    >
                      MLS Listing Source
                    </Text>
                    {mlsListingForNewPropertyLoading ? (
                      <Skeleton height="32px" />
                    ) : (
                      <RadioGroup
                        value={selectedMlsListingIdForNewProperty ?? ''}
                        onChange={(value) =>
                          onMlsListingChangeForNewProperty?.(value || null)
                        }
                      >
                        <VStack align="start" spacing={2}>
                          {mlsListingForNewProperty?.rankedListings.map(
                            (ranked) => (
                              <Radio
                                key={ranked.listing.id}
                                colorScheme="blue"
                                size="sm"
                                value={ranked.listing.id}
                              >
                                <VStack align="start" spacing={0}>
                                  <Text
                                    color="compliance.text.muted"
                                    fontSize="sm"
                                  >
                                    {ranked.listing.addressFull ??
                                      ranked.listing.listingId}
                                    {ranked.listing.id ===
                                      mlsListingForNewProperty?.selectedListing
                                        ?.id && (
                                      <Text
                                        as="span"
                                        color="green.500"
                                        fontSize="xs"
                                        ml={1}
                                      >
                                        (Recommended)
                                      </Text>
                                    )}
                                  </Text>
                                  <Text
                                    color="compliance.text.secondary"
                                    fontSize="xs"
                                  >
                                    {ranked.listing.listingId &&
                                      `#${ranked.listing.listingId} • `}
                                    {ranked.listing.mls?.originatingSystemName}{' '}
                                    • {ranked.listing.mls?.statusText} •{' '}
                                    {ranked.listing.property?.type}
                                    {ranked.listing.listDate &&
                                      ` • Listed ${dayjs(
                                        ranked.listing.listDate
                                      ).format('M/D/YYYY')}`}
                                  </Text>
                                </VStack>
                              </Radio>
                            )
                          )}
                        </VStack>
                      </RadioGroup>
                    )}
                  </VStack>
                )}

              {/* Read-only MLS Listing info for update mode */}
              {!isCreatingDarwinProperty &&
                selectedDarwinProperty?.mlsListing && (
                  <VStack align="stretch" spacing={2}>
                    <Text
                      color="compliance.text.primary"
                      fontSize="xs"
                      fontWeight="semibold"
                      textTransform="uppercase"
                    >
                      MLS Listing
                    </Text>
                    <Box
                      bg="compliance.background.secondary"
                      borderRadius="md"
                      p={3}
                    >
                      <VStack align="start" spacing={0}>
                        <Text color="compliance.text.muted" fontSize="sm">
                          {selectedDarwinProperty.mlsListing.addressFull ??
                            selectedDarwinProperty.mlsListing.listingId}
                        </Text>
                        <Text color="compliance.text.secondary" fontSize="xs">
                          {selectedDarwinProperty.mlsListing.listingId &&
                            `#${selectedDarwinProperty.mlsListing.listingId} • `}
                          {
                            selectedDarwinProperty.mlsListing.mls
                              ?.originatingSystemName
                          }{' '}
                          • {selectedDarwinProperty.mlsListing.mls?.statusText}{' '}
                          • {selectedDarwinProperty.mlsListing.property?.type}
                          {selectedDarwinProperty.mlsListing.listDate &&
                            ` • Listed ${dayjs(
                              selectedDarwinProperty.mlsListing.listDate
                            ).format('M/D/YYYY')}`}
                        </Text>
                      </VStack>
                    </Box>
                  </VStack>
                )}

              {/* Show skeleton while sync preview is loading */}
              {syncPreviewLoading ? (
                <VStack align="stretch" spacing={3}>
                  <Skeleton height="16px" width="120px" />
                  <Skeleton height="24px" />
                  <Skeleton height="24px" />
                  <Skeleton height="24px" />
                  <Skeleton height="16px" mt={2} width="100px" />
                  <Skeleton height="24px" />
                  <Skeleton height="24px" />
                </VStack>
              ) : (
                <>
                  {visibleKeyTermKeys.length > 0 && (
                    <VStack align="stretch" spacing={3}>
                      <Text
                        color="compliance.text.primary"
                        fontSize="xs"
                        fontWeight="semibold"
                        textTransform="uppercase"
                      >
                        Purchase Details
                      </Text>
                      {visibleKeyTermKeys.flatMap((fieldKey) => {
                        const comparisons = getFieldComparisons(
                          fieldKey as DarwinEnabledNormalizedFields
                        );

                        // Create a separate checkbox for each Darwin field
                        return comparisons.map((comparison) => {
                          // Composite key to uniquely identify each Darwin field
                          const compositeKey = buildCompositeKey(
                            fieldKey,
                            comparison.darwinKey
                          );

                          return (
                            <Flex
                              key={compositeKey}
                              align="center"
                              justify="space-between"
                              w="100%"
                            >
                              <Checkbox
                                colorScheme="blue"
                                isChecked={selectedFields.includes(
                                  compositeKey
                                )}
                                onChange={() => handleFieldToggle(compositeKey)}
                              >
                                <Text
                                  color="compliance.text.muted"
                                  fontSize="sm"
                                  textTransform="none"
                                >
                                  {comparison.displayName}
                                </Text>
                              </Checkbox>

                              <Text
                                color="compliance.text.muted"
                                fontSize="xs"
                                textAlign="right"
                              >
                                {comparison.hasChange ? (
                                  <>
                                    <Text
                                      as="s"
                                      color="compliance.text.muted"
                                      display="inline"
                                    >
                                      {String(
                                        comparison.darwinValue ||
                                          DEFAULT_DISPLAY_VALUE
                                      )}
                                    </Text>{' '}
                                    →{' '}
                                    <Text as="b" display="inline">
                                      {comparison.keyTermValue}
                                    </Text>
                                  </>
                                ) : (
                                  <Text display="inline">
                                    {comparison.keyTermValue} → No change
                                  </Text>
                                )}
                              </Text>
                            </Flex>
                          );
                        });
                      })}
                    </VStack>
                  )}

                  {(hasComputedStatus ||
                    (!isSellSide &&
                      !isCreatingDarwinProperty &&
                      propertyDetailComparisons.length > 0)) && (
                    <VStack align="stretch" spacing={3}>
                      <Text
                        color="compliance.text.primary"
                        fontSize="xs"
                        fontWeight="semibold"
                        textTransform="uppercase"
                      >
                        Property Details
                      </Text>

                      {/* Status sync - shown first (BLO-2795) */}
                      {hasComputedStatus && (
                        <Flex align="center" justify="space-between" w="100%">
                          <Tooltip
                            shouldWrapChildren
                            isDisabled={
                              !isStatusUnknownFallback && !isStatusRequired
                            }
                            label={
                              isStatusRequired
                                ? 'Status is required when creating a new property'
                                : statusSyncPreview?.proposedValue
                                    .warningMessage
                            }
                            placement="top"
                          >
                            <Checkbox
                              colorScheme="blue"
                              isChecked={isStatusRequired || shouldSyncStatus}
                              isDisabled={
                                !hasComputedStatus || isStatusRequired
                              }
                              onChange={() =>
                                setShouldSyncStatus((prev) => !prev)
                              }
                            >
                              <Flex align="center" gap={1}>
                                <Text
                                  color={
                                    isStatusUnknownFallback
                                      ? 'yellow.500'
                                      : 'compliance.text.muted'
                                  }
                                  fontSize="sm"
                                  fontStyle={
                                    isStatusUnknownFallback
                                      ? 'italic'
                                      : 'normal'
                                  }
                                  textTransform="none"
                                >
                                  Status
                                </Text>
                                {isStatusRequired && (
                                  <Icon
                                    as={Lock}
                                    boxSize={3}
                                    color="compliance.text.muted"
                                  />
                                )}
                                {isStatusUnknownFallback &&
                                  !isStatusRequired && (
                                    <Icon
                                      as={TriangleAlert}
                                      boxSize={3}
                                      color="yellow.500"
                                    />
                                  )}
                              </Flex>
                            </Checkbox>
                          </Tooltip>

                          <Text
                            color="compliance.text.muted"
                            fontSize="xs"
                            textAlign="right"
                          >
                            {statusComparison.hasChange ? (
                              <>
                                <Text
                                  as="s"
                                  color="compliance.text.muted"
                                  display="inline"
                                >
                                  {statusComparison.darwinValue}
                                </Text>{' '}
                                →{' '}
                                <Text as="b" display="inline">
                                  {statusComparison.computedValue}
                                </Text>
                              </>
                            ) : (
                              <Text display="inline">
                                {statusComparison.computedValue} → No change
                              </Text>
                            )}
                          </Text>
                        </Flex>
                      )}

                      {/* Property details sync (type code, property type, classification) */}
                      {!isSellSide &&
                        !isCreatingDarwinProperty &&
                        propertyDetailComparisons.length > 0 && (
                          <>
                            <Checkbox
                              colorScheme="blue"
                              isChecked={
                                !isSellSide && shouldSyncPropertyDetails
                              }
                              onChange={() =>
                                setShouldSyncPropertyDetails((prev) => !prev)
                              }
                            >
                              <Text
                                color="compliance.text.muted"
                                fontSize="sm"
                                textTransform="none"
                              >
                                Sync property details
                              </Text>
                            </Checkbox>

                            <VStack align="stretch" pl={6} spacing={2}>
                              {propertyDetailComparisons.map((comparison) => (
                                <Flex
                                  key={comparison.key}
                                  align="center"
                                  justify="space-between"
                                  w="100%"
                                >
                                  <Text
                                    color="compliance.text.muted"
                                    fontSize="sm"
                                    textTransform="none"
                                  >
                                    {comparison.label}
                                  </Text>

                                  <Text
                                    color="compliance.text.muted"
                                    fontSize="xs"
                                    textAlign="right"
                                  >
                                    {comparison.hasChange ? (
                                      <>
                                        <Text
                                          as="s"
                                          color="compliance.text.muted"
                                          display="inline"
                                        >
                                          {comparison.darwinValue}
                                        </Text>{' '}
                                        →{' '}
                                        <Text as="b" display="inline">
                                          {comparison.complianceValue}
                                        </Text>
                                      </>
                                    ) : (
                                      <Text display="inline">
                                        {comparison.complianceValue} → No change
                                      </Text>
                                    )}
                                  </Text>
                                </Flex>
                              ))}
                            </VStack>
                          </>
                        )}
                    </VStack>
                  )}

                  {/* Vendors section - Lender and Title Company with click-to-expand */}
                  {vendorsSyncPreview?.fields &&
                    vendorsSyncPreview.fields.length > 0 && (
                      <VStack align="stretch" spacing={3}>
                        <Text
                          color="compliance.text.primary"
                          fontSize="xs"
                          fontWeight="semibold"
                          textTransform="uppercase"
                        >
                          Vendors
                        </Text>
                        {vendorsSyncPreview.fields.map((field) => {
                          const isDisabled =
                            field.proposedValue.disabledOptions?.isDisabled ===
                            true;
                          const currentName =
                            field.currentValue?.personName ??
                            DEFAULT_DISPLAY_VALUE;
                          const candidates = getVendorCandidates(field);
                          const selectedPersonId = getSelectedPersonId(field);
                          const isExpanded = expandedVendors.has(field.label);
                          const hasAlternatives = candidates.length > 1;

                          // Find selected candidate name for display
                          const selectedCandidate = candidates.find(
                            (c) => c.personId === selectedPersonId
                          );
                          const selectedName =
                            selectedCandidate?.personName ??
                            DEFAULT_DISPLAY_VALUE;

                          // Side display: 'L' = List Side, 'S' = Buy Side
                          const formatSide = (
                            side: string | null | undefined
                          ) => {
                            if (side === 'L') return 'List Side';
                            if (side === 'S') return 'Buy Side';

                            return null;
                          };
                          const currentSide = formatSide(
                            field.currentValue?.side
                          );
                          const proposedSide = formatSide(
                            field.proposedValue.side
                          );

                          // Use disabledReason from backend, fallback to generic message
                          const tooltipLabel = isDisabled
                            ? field.proposedValue.disabledReason ||
                              'No value extracted from documents'
                            : undefined;

                          // Determine if there's an effective change based on user selection
                          // (not just the backend's initial hasChange which is based on best match)
                          const hasEffectiveChange =
                            !isDisabled &&
                            selectedPersonId !== field.currentValue?.personId;

                          return (
                            <Box key={field.label} w="100%">
                              {/* Main row with checkbox and value display */}
                              <Flex
                                align="center"
                                justify="space-between"
                                w="100%"
                              >
                                <Tooltip
                                  shouldWrapChildren
                                  isDisabled={!isDisabled}
                                  label={tooltipLabel}
                                  placement="top"
                                >
                                  <Checkbox
                                    colorScheme="blue"
                                    isChecked={selectedVendorFields.includes(
                                      field.label
                                    )}
                                    isDisabled={isDisabled}
                                    onChange={() =>
                                      handleVendorFieldToggle(field.label)
                                    }
                                  >
                                    <Text
                                      color={
                                        isDisabled
                                          ? 'compliance.text.disabled'
                                          : 'compliance.text.muted'
                                      }
                                      fontSize="sm"
                                      textTransform="none"
                                    >
                                      {field.label}
                                    </Text>
                                  </Checkbox>
                                </Tooltip>

                                <Flex align="center" gap={1}>
                                  <Text
                                    color={
                                      isDisabled
                                        ? 'compliance.text.disabled'
                                        : 'compliance.text.muted'
                                    }
                                    fontSize="xs"
                                    textAlign="right"
                                  >
                                    {hasEffectiveChange ? (
                                      <>
                                        <Text
                                          as="s"
                                          color="compliance.text.muted"
                                          display="inline"
                                        >
                                          {currentName}
                                          {currentSide && ` (${currentSide})`}
                                        </Text>{' '}
                                        →{' '}
                                        <Text as="b" display="inline">
                                          {selectedName}
                                          {proposedSide && ` (${proposedSide})`}
                                        </Text>
                                      </>
                                    ) : (
                                      <Text display="inline">
                                        {currentName}
                                        {currentSide && ` (${currentSide})`} →
                                        No change
                                      </Text>
                                    )}
                                  </Text>
                                  {/* Expand/collapse button for alternatives */}
                                  {hasAlternatives && !isDisabled && (
                                    <Button
                                      aria-label={
                                        isExpanded
                                          ? 'Collapse alternatives'
                                          : 'Expand alternatives'
                                      }
                                      minW="auto"
                                      p={0}
                                      size="xs"
                                      variant="ghost"
                                      onClick={() =>
                                        toggleVendorExpanded(field.label)
                                      }
                                    >
                                      <Icon
                                        as={
                                          isExpanded ? ChevronUp : ChevronDown
                                        }
                                        boxSize={4}
                                        color="compliance.text.muted"
                                      />
                                    </Button>
                                  )}
                                </Flex>
                              </Flex>

                              {/* Expanded alternatives selection */}
                              {isExpanded && hasAlternatives && !isDisabled && (
                                <Box
                                  bg="compliance.bg.secondary"
                                  borderRadius="md"
                                  ml={6}
                                  mt={2}
                                  p={2}
                                >
                                  <Text
                                    color="compliance.text.muted"
                                    fontSize="xs"
                                    mb={2}
                                  >
                                    Select match:
                                  </Text>
                                  <RadioGroup
                                    value={selectedPersonId?.toString() ?? ''}
                                    onChange={(value) =>
                                      handleVendorPersonChange(
                                        field.vendorType,
                                        parseInt(value, 10)
                                      )
                                    }
                                  >
                                    <VStack align="start" spacing={1}>
                                      {candidates.map((candidate) => (
                                        <Radio
                                          key={candidate.personId}
                                          colorScheme="blue"
                                          size="sm"
                                          value={candidate.personId.toString()}
                                        >
                                          <Text
                                            color="compliance.text.muted"
                                            fontSize="xs"
                                          >
                                            {candidate.personName}
                                            {proposedSide &&
                                              ` (${proposedSide})`}{' '}
                                            <Text
                                              as="span"
                                              color="compliance.text.secondary"
                                              fontSize="xs"
                                            >
                                              ID: {candidate.personId}
                                            </Text>
                                          </Text>
                                        </Radio>
                                      ))}
                                    </VStack>
                                  </RadioGroup>
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                      </VStack>
                    )}

                  {/* Fees section - Transaction Management Fee and future fees */}
                  {feesSyncPreview?.fields &&
                    feesSyncPreview.fields.length > 0 && (
                      <VStack align="stretch" spacing={3}>
                        <Text
                          color="compliance.text.primary"
                          fontSize="xs"
                          fontWeight="semibold"
                          textTransform="uppercase"
                        >
                          Fees
                        </Text>
                        {feesSyncPreview.fields.map((field) => {
                          const isDisabled =
                            field.proposedValue.disabledOptions?.isDisabled ===
                            true;
                          const isSelected = selectedFeeTypes.has(
                            field.feeType
                          );

                          // Format fee display: "$500 (Agent paid)" or "$500 (Company paid)"
                          const formatFee = (
                            amount: number | null | undefined,
                            payer?: string | null
                          ) => {
                            if (amount == null) return DEFAULT_DISPLAY_VALUE;
                            const formatted = formatToCurrency(amount);
                            if (payer) {
                              const payerLabel =
                                payer === 'agent'
                                  ? 'Agent paid'
                                  : 'Company paid';

                              return `${formatted} (${payerLabel})`;
                            }

                            return formatted;
                          };

                          const currentAmount = field.currentValue?.amount;
                          const proposedAmount = field.proposedValue.amount;
                          const payer = field.proposedValue.payer;

                          return (
                            <Flex
                              key={field.feeType}
                              align="center"
                              justify="space-between"
                              w="100%"
                            >
                              <Tooltip
                                shouldWrapChildren
                                isDisabled={!isDisabled}
                                label="No fee value extracted from documents"
                                placement="top"
                              >
                                <Checkbox
                                  colorScheme="blue"
                                  isChecked={isSelected}
                                  isDisabled={isDisabled}
                                  onChange={() =>
                                    toggleFeeSelected(field.feeType)
                                  }
                                >
                                  <Text
                                    color={
                                      isDisabled
                                        ? 'compliance.text.disabled'
                                        : 'compliance.text.muted'
                                    }
                                    fontSize="sm"
                                    textTransform="none"
                                  >
                                    {field.label}
                                  </Text>
                                </Checkbox>
                              </Tooltip>

                              <Text
                                color={
                                  isDisabled
                                    ? 'compliance.text.disabled'
                                    : 'compliance.text.muted'
                                }
                                fontSize="xs"
                                textAlign="right"
                              >
                                {field.hasChange ? (
                                  <>
                                    <Text
                                      as="s"
                                      color="compliance.text.muted"
                                      display="inline"
                                    >
                                      {formatFee(currentAmount)}
                                    </Text>{' '}
                                    →{' '}
                                    <Text as="b" display="inline">
                                      {formatFee(proposedAmount, payer)}
                                    </Text>
                                  </>
                                ) : (
                                  <Text display="inline">
                                    {formatFee(proposedAmount, payer)} → No
                                    change
                                  </Text>
                                )}
                              </Text>
                            </Flex>
                          );
                        })}
                      </VStack>
                    )}

                  {participants.length > 0 && (
                    <VStack align="stretch" spacing={3}>
                      <Text
                        color="compliance.text.primary"
                        fontSize="xs"
                        fontWeight="semibold"
                        textTransform="uppercase"
                      >
                        Participants
                      </Text>
                      {participants.map((participant) => {
                        const comparison =
                          getParticipantComparison(participant);

                        return (
                          <Flex
                            key={participant.role}
                            align="center"
                            justify="space-between"
                            w="100%"
                          >
                            <Checkbox
                              colorScheme="blue"
                              isChecked={selectedParticipants.includes(
                                participant.role
                              )}
                              onChange={() =>
                                handleParticipantToggle(participant.role)
                              }
                            >
                              <Text
                                color="compliance.text.muted"
                                fontSize="sm"
                                textTransform="none"
                              >
                                {participant.label}
                              </Text>
                            </Checkbox>

                            <Text
                              color="compliance.text.muted"
                              fontSize="xs"
                              textAlign="right"
                            >
                              {comparison.hasChange ? (
                                <>
                                  <Text
                                    as="s"
                                    color="compliance.text.muted"
                                    display="inline"
                                  >
                                    {comparison.darwinValue ||
                                      DEFAULT_DISPLAY_VALUE}
                                  </Text>{' '}
                                  →{' '}
                                  <Text as="b" display="inline">
                                    {comparison.participantValue}
                                  </Text>
                                </>
                              ) : (
                                <Text display="inline">
                                  {comparison.participantValue} → No change
                                </Text>
                              )}
                            </Text>
                          </Flex>
                        );
                      })}
                    </VStack>
                  )}
                </>
              )}

              <Tooltip
                shouldWrapChildren
                isDisabled={variant !== 'internal'}
                label="Writing to Darwin is disabled in the internal debug tool"
                placement="top"
              >
                <Button
                  isDisabled={
                    variant === 'internal' || totalSelections === 0 || !onUpload
                  }
                  isLoading={isUploading}
                  loadingText="Writing to Darwin..."
                  size="sm"
                  onClick={() => void handleUpload()}
                >
                  Write to Darwin ({totalSelections} items)
                </Button>
              </Tooltip>
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
